// 02A · Apport SCO — ce qu'il faut recharger dans les monnayeurs des caisses
// automatiques, déduit du ticket « Vérification du tiroir » de la nuit.
//
// L'extension ORIKA transporte le ticket, FlashCP calcule. Même partage que pour
// les CSV, et pour la même raison : un seuil ou une taille de rouleau qui change
// se corrige côté serveur, sans réinstaller quoi que ce soit sur le poste.
//
// L'extension applique déjà les quatre contrôles avant de déposer. On les refait
// ici quand même : le dépôt peut venir d'ailleurs, et un contrôle qui ne coûte
// rien ne se délègue pas à un artefact installé à la main.

import { APPORT_ROULEAUX } from 'sotcp-core/inventaireCoffreCalc'

// ── Les règles, dictées par le poste ───────────────────────────────────────

export interface ReglesSco {
  /** En dessous de ce nombre de pièces, on recharge. */
  seuilPieces: number
  /** En dessous de ce nombre de billets, on recharge. */
  seuilBillets: number
  /** Premier apport de billets. */
  billetsInitial: number
  /** Puis on complète par paliers de cette taille. */
  billetsPalier: number
  /** Billets rechargés par la machine. Les 50, 100, 200 et 500 € sont acceptés mais pas distribués. */
  billetsRecharges: number[]
  /** Au-dessus du seuil mais en dessous d'ici : le stock tiendra à peine. */
  vigilancePieces: number
  vigilanceBillets: number
}

export const REGLES_SCO_DEFAUT: ReglesSco = {
  seuilPieces: 100,
  seuilBillets: 30,
  billetsInitial: 10,
  billetsPalier: 5,
  billetsRecharges: [5, 10, 20],
  vigilancePieces: 150,
  vigilanceBillets: 40,
}

/**
 * Nombre de pièces par rouleau, DÉDUIT de la valorisation bancaire partagée
 * plutôt que ressaisi : `valeurRouleau / denomination`. Le 10 c à 40 pièces et
 * le 1 € à 25 sont les deux exceptions qui produisent les régressions
 * classiques — elles n'existent qu'à un seul endroit, dans `sotcp-core`, d'où
 * l'inventaire de coffre et les apports caissiers les tirent déjà.
 */
export const PIECES_PAR_ROULEAU: Record<string, number> = Object.fromEntries(
  APPORT_ROULEAUX.map((r) => [r.denomination.toFixed(2), Math.round(r.valeurRouleau / r.denomination)]),
)

/** Les quinze dénominations d'un tiroir, des centimes aux gros billets. */
export const DENOMINATIONS = [0.01, 0.02, 0.05, 0.10, 0.20, 0.50, 1, 2, 5, 10, 20, 50, 100, 200, 500]

// ── L'algorithme d'apport ──────────────────────────────────────────────────

/**
 * Pièces : en dessous du seuil, on ajoute un rouleau ; si un rouleau ne suffit
 * pas à repasser au-dessus, on en ajoute autant que nécessaire.
 *
 * Contrôle du poste : 2 € à 70 pièces. Un rouleau de 25 donnerait 95 —
 * insuffisant. Donc deux rouleaux, soit 120.
 */
export function rouleauxAAjouter(compte: number, taille: number, seuil: number): number {
  if (!(taille > 0)) return 0
  if (compte >= seuil) return 0
  return Math.ceil((seuil - compte) / taille)
}

/**
 * Billets : en dessous du seuil on ajoute le paquet initial ; s'il ne suffit
 * pas, on complète par paliers.
 *
 * Contrôles du poste : 25 → 10 (35) · 15 → 15 (30) · 5 → 25 (30) · 29 → 10 (39).
 */
export function billetsAAjouter(
  compte: number, seuil: number, initial: number, palier: number,
): number {
  if (compte >= seuil) return 0
  const besoin = seuil - compte
  if (besoin <= initial) return initial
  if (!(palier > 0)) return initial
  return initial + palier * Math.ceil((besoin - initial) / palier)
}

// ── L'état d'un stock ──────────────────────────────────────────────────────

/**
 * Le niveau d'une dénomination dans la machine, en trois bandes :
 *
 *   sous le seuil            → rouge, il faut recharger ;
 *   du seuil à la vigilance  → orange, ça tiendra tout juste ;
 *   au-dessus                → vert, rien à surveiller.
 *
 * Le vert est ici légitime, contrairement à un écart (D14) : ce n'est pas une
 * différence à expliquer, c'est un stock qui va bien. Les bandes sont
 * CONTIGUËS — un compte de 32 billets n'est pas « vert » parce qu'il tombe
 * entre deux bornes ; toute valeur au-dessus du seuil et sous la vigilance est
 * orange.
 *
 * Une dénomination que la machine ne recharge pas n'a pas de niveau : personne
 * ne décide rien à son sujet, la colorer serait bruit.
 */
export type Niveau = 'stop' | 'warn' | 'ok'

export function niveauCompte(
  compte: number, nature: 'rouleau' | 'billet', rechargeable: boolean,
  regles: ReglesSco = REGLES_SCO_DEFAUT,
): Niveau | null {
  if (!rechargeable) return null
  const seuil = nature === 'rouleau' ? regles.seuilPieces : regles.seuilBillets
  const vigilance = nature === 'rouleau' ? regles.vigilancePieces : regles.vigilanceBillets
  if (compte < seuil) return 'stop'
  return compte < vigilance ? 'warn' : 'ok'
}

// ── Le ticket ──────────────────────────────────────────────────────────────

export interface LigneTiroir {
  denomination: number
  libelle: string
  nombre: number
  unitaire: number
  total: number
}

export interface TicketTiroir {
  caisse: number | null
  ticket: number | null
  date: string | null           // AAAA-MM-JJ, recomposée
  heure: string | null
  libelleType: string
  sco: boolean
  netTTC: number | null
  reference: string | null
  lignes: LigneTiroir[]
  /** Somme des lignes de dénomination. */
  totalLignes: number
  /** La ligne TOTAL du ticket (typl 4), si elle a été trouvée. */
  totalTicket: number | null
}

const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}

/**
 * Les nombres du ticket emploient le POINT décimal (« 3.02 ») tandis que
 * `netttc` emploie la virgule et une espace de milliers (« 3 198,03 € »).
 * Les deux passent par ici.
 */
export function nombre(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v !== 'string') return null
  const net = v.replace(/[\s  ]/g, '').replace(/€|EUR/gi, '')
  // « 3.02 » et « 3,02 » désignent la même chose ; aucun ticket n'emploie les
  // deux séparateurs à la fois.
  const n = Number(net.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

const LIGNE_DENOM =
  /^(?<lib>.+?)\s{2,}(?<nb>\d+)\s*x\s*(?<pu>[\d.,]+)\s*EUR\s+(?<tot>[\d.,]+)\s*EUR$/

/** `typl` 193 = ligne de dénomination · 4 = ligne TOTAL. */
const TYPL_DENOM = 193
const TYPL_TOTAL = 4

/**
 * Le texte d'une ligne de ticket. ORIKA ne documente pas le nom du champ : on
 * essaie chaque propriété de type chaîne plutôt que d'en supposer une. Ce qui
 * identifie une ligne, c'est `typl`, pas sa position ni son nom de champ.
 */
function texteLigne(ligne: Record<string, unknown>): string[] {
  return Object.values(ligne).filter((v): v is string => typeof v === 'string')
}

/**
 * Lit le dépôt : le tableau `dashboard[0].data` d'ORIKA, tel quel. On accepte
 * aussi bien le tableau que l'élément seul — un dépôt qui a perdu son
 * enveloppe en route reste exploitable.
 */
export function lireTicket(brut: unknown): TicketTiroir | null {
  const racine = Array.isArray(brut) ? brut[0] : brut
  const t = obj(racine)
  if (Object.keys(t).length === 0) return null

  const lignes: LigneTiroir[] = []
  let totalTicket: number | null = null

  const recu = t.print_receipt
  const contenu: unknown = typeof recu === 'string' ? tenter(() => JSON.parse(recu)) : recu
  const listes = Array.isArray(contenu) ? contenu : Array.isArray(obj(contenu).lignes) ? obj(contenu).lignes : []

  for (const l of Array.isArray(listes) ? listes : []) {
    const ligne = obj(l)
    const typl = Number(ligne.typl)
    if (typl === TYPL_TOTAL && totalTicket === null) {
      for (const s of texteLigne(ligne)) {
        const m = /([\d.,\s  ]+)\s*EUR\s*$/.exec(s.trim())
        const n = m ? nombre(m[1]) : null
        if (n !== null) { totalTicket = n; break }
      }
      continue
    }
    if (typl !== TYPL_DENOM) continue
    for (const s of texteLigne(ligne)) {
      const m = LIGNE_DENOM.exec(s.trim())
      if (!m?.groups) continue
      const unitaire = nombre(m.groups.pu)
      const nb = nombre(m.groups.nb)
      const tot = nombre(m.groups.tot)
      if (unitaire === null || nb === null || tot === null) continue
      lignes.push({
        denomination: unitaire,
        libelle: (m.groups.lib ?? '').trim(),
        nombre: Math.round(nb),
        unitaire,
        total: tot,
      })
      break
    }
  }

  const ref = typeof t.reftkt === 'string' ? t.reftkt : null
  const decomp = ref ? decomposerReference(ref) : null

  return {
    caisse: Number.isFinite(Number(t.idc)) ? Number(t.idc) : decomp?.tpv ?? null,
    ticket: Number.isFinite(Number(t.idt)) ? Number(t.idt) : decomp?.ticket ?? null,
    date: dateMetier(typeof t.dt === 'string' ? t.dt : null) ?? decomp?.date ?? null,
    heure: typeof t.hdeb === 'string' ? t.hdeb : decomp?.heure ?? null,
    libelleType: String(t.typt ?? ''),
    sco: Number(t.typsco) === 1,
    netTTC: nombre(t.netttc),
    reference: ref,
    lignes,
    totalLignes: +lignes.reduce((s, l) => s + l.total, 0).toFixed(2),
    totalTicket,
  }
}

const tenter = <T,>(f: () => T): T | null => { try { return f() } catch { return null } }

/** « 12-08-2026 » → « 2026-08-12 ». */
export function dateMetier(dt: string | null): string | null {
  if (!dt) return null
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dt.trim())
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (/^\d{4}-\d{2}-\d{2}$/.test(dt.trim()) ? dt.trim() : null)
}

export interface Reference {
  magasin: string; tpv: number; ticket: number; date: string; heure: string
}

/**
 * `reftkt` est auto-porteuse : magasin (4) + TPV (3) + ticket (5) + AAMMJJ (6)
 * + HHMMSS (6). C'est un contrôle indépendant des autres champs du ticket.
 */
export function decomposerReference(ref: string): Reference | null {
  const r = ref.trim()
  if (!/^\d{24}$/.test(r)) return null
  const aa = r.slice(12, 14), mm = r.slice(14, 16), jj = r.slice(16, 18)
  const hh = r.slice(18, 20), mi = r.slice(20, 22), ss = r.slice(22, 24)
  return {
    magasin: r.slice(0, 4),
    tpv: Number(r.slice(4, 7)),
    ticket: Number(r.slice(7, 12)),
    date: `20${aa}-${mm}-${jj}`,
    heure: `${hh}:${mi}:${ss}`,
  }
}

// ── Les quatre contrôles ───────────────────────────────────────────────────

export interface Controle { cle: string; ok: boolean; libelle: string; detail: string }

/** Un centime de tolérance : les tickets arrondissent, pas nous. */
const CENTIME = 0.011

export function controler(t: TicketTiroir, journee?: string): Controle[] {
  const decomp = t.reference ? decomposerReference(t.reference) : null
  const sommeOk = t.totalTicket === null || Math.abs(t.totalLignes - t.totalTicket) <= CENTIME
  const netOk = t.netTTC === null || Math.abs(t.totalLignes - t.netTTC) <= CENTIME

  // Cohérence INTERNE de chaque ligne : nombre × unitaire doit tomber sur le
  // total imprimé. Sans elle, fausser le NOMBRE de pièces passait inaperçu —
  // la somme des totaux imprimés restait juste, et c'est pourtant le nombre qui
  // commande l'apport. C'est cette vérification qui rattrape les 6,97 € du cas
  // d'essai (999 pièces annoncées au lieu de 302).
  const incoherentes = t.lignes.filter(
    (l) => Math.abs(l.nombre * l.unitaire - l.total) > CENTIME,
  )

  return [
    {
      cle: 'type', ok: /tiroir/i.test(t.libelleType),
      libelle: 'C’est bien une vérification de tiroir',
      // Le code numérique du type n'est pas digne de confiance : c'est le
      // libellé en clair qui fait foi.
      detail: t.libelleType || 'libellé absent',
    },
    {
      cle: 'reference',
      ok: !!decomp
        && (t.ticket === null || decomp.ticket === t.ticket)
        && (t.caisse === null || decomp.tpv === t.caisse)
        && (!journee || decomp.date === journee),
      libelle: 'La référence concorde avec la date et la caisse',
      detail: decomp
        ? `${decomp.magasin} · TPV ${decomp.tpv} · ticket ${decomp.ticket} · ${decomp.date} ${decomp.heure}`
        : 'référence illisible',
    },
    {
      cle: 'lignes', ok: t.lignes.length >= 10,
      libelle: 'Le détail des dénominations est complet',
      detail: `${t.lignes.length} dénomination${t.lignes.length > 1 ? 's' : ''} lue${t.lignes.length > 1 ? 's' : ''}`,
    },
    {
      // Le vrai filet : fausser une ligne du ticket se voit ici, et nulle part ailleurs.
      cle: 'comptable', ok: sommeOk && netOk && incoherentes.length === 0,
      libelle: 'Chaque ligne est cohérente et la somme tombe sur le total',
      detail: incoherentes.length > 0
        ? `${incoherentes.length} ligne(s) dont le nombre ne tombe pas sur le total : `
          + incoherentes.map((l) => `${l.libelle} (${l.nombre} × ${l.unitaire} ≠ ${l.total})`).join(', ')
        : `lignes ${t.totalLignes.toFixed(2)} €`
          + (t.totalTicket !== null ? ` · TOTAL ${t.totalTicket.toFixed(2)} €` : '')
          + (t.netTTC !== null ? ` · net TTC ${t.netTTC.toFixed(2)} €` : ''),
    },
  ]
}

export const ticketExploitable = (c: Controle[]): boolean => c.every((x) => x.ok)

// ── Ce qu'il faut recharger ────────────────────────────────────────────────

export interface LigneApportSco {
  denomination: number
  libelle: string
  /** Ce que la machine contient. */
  compte: number
  /** Rechargeable par la machine ? Les gros billets ne le sont pas. */
  rechargeable: boolean
  /** Nature de l'apport : un rouleau de pièces ou des billets à l'unité. */
  nature: 'rouleau' | 'billet'
  /** Ce que l'algorithme proposait, avant toute reprise en main. */
  propose: number
  /** La quantité retenue diffère de la proposition. */
  ajuste: boolean
  /** Nombre de rouleaux, ou nombre de billets — la quantité RETENUE. */
  quantite: number
  /** Pièces ou billets réellement ajoutés. */
  unites: number
  /** Compte après rechargement. */
  apres: number
  /** Valeur de l'apport, en euros. */
  valeur: number
}

export interface ApportSco {
  lignes: LigneApportSco[]
  /** Valeur totale à charger dans cette machine. */
  valeur: number
  /** Nombre de dénominations à recharger. */
  aRecharger: number
  /** Nombre de dénominations où la décision s'écarte de la proposition. */
  ajustees: number
}

/**
 * Les quantités retenues, dénomination par dénomination — « 2.00 » → 3 rouleaux.
 * L'algorithme propose, ceci décide : une valeur présente ici REMPLACE la
 * proposition, y compris quand elle vaut zéro (« finalement, ne rien mettre »).
 */
export type Ajustements = Record<string, number>

export function calculerApport(
  t: TicketTiroir, regles = REGLES_SCO_DEFAUT, ajuste: Ajustements = {},
): ApportSco {
  const parDenom = new Map(t.lignes.map((l) => [l.denomination.toFixed(2), l]))
  const lignes: LigneApportSco[] = []

  for (const d of DENOMINATIONS) {
    const cle = d.toFixed(2)
    const lue = parDenom.get(cle)
    const compte = lue?.nombre ?? 0
    const libelle = lue?.libelle ?? etiquette(d)
    const taille = PIECES_PAR_ROULEAU[cle]

    const impose = Object.prototype.hasOwnProperty.call(ajuste, cle)
      ? Math.max(0, Math.round(Number(ajuste[cle]) || 0))
      : null

    if (taille) {
      const propose = rouleauxAAjouter(compte, taille, regles.seuilPieces)
      const rouleaux = impose ?? propose
      const unites = rouleaux * taille
      lignes.push({
        denomination: d, libelle, compte, rechargeable: true, nature: 'rouleau',
        propose, ajuste: impose !== null && impose !== propose,
        quantite: rouleaux, unites, apres: compte + unites,
        valeur: +(unites * d).toFixed(2),
      })
      continue
    }

    const recharge = regles.billetsRecharges.includes(d)
    const propose = recharge
      ? billetsAAjouter(compte, regles.seuilBillets, regles.billetsInitial, regles.billetsPalier)
      : 0
    const billets = impose ?? propose
    lignes.push({
      denomination: d, libelle, compte, rechargeable: recharge, nature: 'billet',
      propose, ajuste: impose !== null && impose !== propose,
      quantite: billets, unites: billets, apres: compte + billets,
      valeur: +(billets * d).toFixed(2),
    })
  }

  return {
    lignes,
    valeur: +lignes.reduce((s, l) => s + l.valeur, 0).toFixed(2),
    aRecharger: lignes.filter((l) => l.quantite > 0).length,
    ajustees: lignes.filter((l) => l.ajuste).length,
  }
}

// ── De la décision à l'apport ──────────────────────────────────────────────

/**
 * L'apport d'une machine, écrit dans la forme que le reste de l'application
 * connaît déjà : `rouleaux` indexé par `r001`…`r200`, `billets` par `b005`…
 *
 * Une différence assumée avec l'apport d'un caissier : les monnayeurs
 * reprennent aussi des billets de 20 €, que le modèle caissier ne connaît pas
 * (il s'arrête à 10 €). D'où `b020`, et d'où le fait que les apports SCO vivent
 * sous `rapport.apportsSco` et non dans `rapport.apports` : les mêler ferait
 * sous-évaluer l'apport de tout le 20 € partout où la V2 le relit.
 */
export interface ApportInscrit {
  caisse: number
  monnayeur: number | null
  rouleaux: Record<string, number>
  billets: Record<string, number>
  valeur: number
  /** L'opérateur a-t-il repris la main sur la proposition ? */
  ajuste: boolean
  inscrit_at: string
  inscrit_par: string | null
}

export const ID_ROULEAU = (d: number): string => `r${String(Math.round(d * 100)).padStart(3, '0')}`
export const ID_BILLET  = (d: number): string => `b${String(Math.round(d)).padStart(3, '0')}`

export function composerApport(a: ApportSco): { rouleaux: Record<string, number>; billets: Record<string, number> } {
  const rouleaux: Record<string, number> = {}
  const billets: Record<string, number> = {}
  for (const l of a.lignes) {
    if (l.quantite <= 0) continue
    if (l.nature === 'rouleau') rouleaux[ID_ROULEAU(l.denomination)] = l.quantite
    else billets[ID_BILLET(l.denomination)] = l.quantite
  }
  return { rouleaux, billets }
}

function etiquette(d: number): string {
  return d < 1 ? `${Math.round(d * 100)} centimes` : `${d} EUR`
}

// ── La correspondance caisse ↔ monnayeur ───────────────────────────────────

export interface Machine { caisse: number; monnayeur: number }

export const MACHINES_DEFAUT: Machine[] = [
  { caisse: 88, monnayeur: 830 },
  { caisse: 89, monnayeur: 840 },
  { caisse: 96, monnayeur: 850 },
  { caisse: 97, monnayeur: 860 },
]

/**
 * « SCO 88 · monnayeur 830 » — les deux identifiants, toujours. Le poste
 * raisonne en SCO sur le terrain, la section Monnayeurs du rapport opérateur
 * parle en 830/840/850/860 : afficher les deux rend le rapprochement immédiat
 * et évite à un remplaçant d'apprendre la correspondance par cœur.
 */
export function nomMachine(caisse: number | null, machines: Machine[]): string {
  if (caisse === null) return 'caisse inconnue'
  const m = machines.find((x) => x.caisse === caisse)
  return m ? `SCO ${m.caisse} · monnayeur ${m.monnayeur}` : `SCO ${caisse} · monnayeur inconnu`
}
